import React, { memo } from 'react';
import Link from 'next/link';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';

import { UiLevelRisk } from '@mais-retorno-front/libs/ui-level-risk';
import { formatNumber } from '@mais-retorno-front/libs/util-helpers';
import { Card, CardHeader } from '@core/design-system/card';
import { DEFAULT_ROUTES } from '@mais-retorno-front/constants/routes';

interface Props {
  fundRelated: {
    nicename: string;
    slug: string;
    volatility_begin: number;
    volatility: any;
    fund_class: {
      class: {
        name: string;
        slug: string;
      };
      subclass: {
        name: string;
        slug: string;
      };
    };
    '12_months_return': number;
    delta: number;
    networth: number;
  }[];
}

export const UiFundRelated = memo<Props>(({ fundRelated = [] }) => {
  if (!fundRelated?.length) return null;

  return (
    <CardHeader
      title="Fundos semelhantes"
      titleVariant="h4"
      cardProps={{ component: 'section', sx: { my: 3, displayPrint: 'none' } }}
    >
      <Grid container spacing={1} alignItems="stretch" mt={2}>
        {fundRelated.map((fund) => (
          <Grid item xs={12} md={4} key={fund.slug}>
            <Card
              cardProps={{
                sx: {
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                },
              }}
            >
              <div>
                <Stack justifyContent="space-between" direction="row" mb={1}>
                  <div>
                    {fund?.fund_class?.class?.name && (
                      <Link
                        href={`${DEFAULT_ROUTES.rankingFund}/${fund.fund_class.class.slug}`}
                        prefetch={false}
                        passHref
                        target="_blank"
                        rel="noopener"
                      >
                        <Typography variant="body2" component="a">
                          {fund.fund_class.class.name}
                        </Typography>
                      </Link>
                    )}

                    {fund?.fund_class?.subclass?.name && (
                      <>
                        {' • '}
                        <Link
                          href={`${DEFAULT_ROUTES.rankingFund}/${fund.fund_class.subclass.slug}`}
                          prefetch={false}
                          passHref
                          target="_blank"
                          rel="noopener"
                        >
                          <Typography variant="body2" component="a">
                            {fund.fund_class.subclass.name}
                          </Typography>
                        </Link>
                      </>
                    )}
                  </div>
                  <div>
                    <UiLevelRisk levelRisk={fund?.volatility?.begin * 100} />
                  </div>
                </Stack>

                <Link
                  href={`${DEFAULT_ROUTES.sheetFunds}/${fund.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Ver lamina do fundo"
                  prefetch={false}
                  passHref
                >
                  <Typography
                    variant="subtitle1"
                    component="a"
                    fontWeight={600}
                    lineHeight={1}
                  >
                    {fund?.nicename}
                  </Typography>
                </Link>
              </div>

              <Stack direction="row" justifyContent="space-between" mt={1}>
                <div style={{ lineHeight: 1 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    component="p"
                    sx={{
                      color: fund?.networth < 0 ? 'error.main' : 'success.main',
                    }}
                  >
                    {formatNumber(fund?.networth, {
                      type: 'money',
                      thousandMask: true,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Patrimônio Líquido
                  </Typography>
                </div>

                <div style={{ lineHeight: 1 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    component="p"
                    sx={{
                      color:
                        fund?.['12_months_return'] < 0
                          ? 'error.main'
                          : 'success.main',
                    }}
                  >
                    {formatNumber(fund?.['12_months_return'] * 100, {
                      type: 'percent',
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Rent. 12 meses
                  </Typography>
                </div>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>
    </CardHeader>
  );
});

export default UiFundRelated;
